/**
 * Visitor Journey Capture — Phase 1 lead intelligence.
 *
 * Records only meaningful, low-cardinality events that help the codelumen
 * team qualify inbound leads. No mouse tracking, no fingerprinting, no
 * per-user profiling beyond a single anonymous session cookie.
 *
 * Storage:
 *  - sessionStorage key `cl_journey_v1` (session-scoped events)
 *  - cookie `cl_visitor` (1 year, uuid) — new vs returning only
 *
 * Consent gate:
 *  A single `journeyEnabled()` centralises the check. Today it defaults to
 *  enabled unless `window.__cl_consent === 'essential-only'` or
 *  `localStorage.cl_consent === 'denied'`. When the site's cookie banner
 *  ships, wire it to that same signal — nothing else in this module
 *  changes.
 *
 * Phase 2 hooks:
 *  Additional event kinds can be appended to `EventKind`; consumers use
 *  the `record()` API by kind + string payload only.
 */

export type EventKind =
  | "page_view"
  | "project_view"
  | "service_view"
  | "cta_click"
  | "consultation_modal_open"
  | "form_start"
  | "form_submit"
  | "form_abandon"
  | "upload_start"
  | "upload_complete"
  | "labs_interest"
  // Project Evaluation Form V2 (behind feature flag). Payloads are
  // strictly non-PII: step number, path, field key, counts, or error
  // class. See src/components/forms/SteppedConsultationFormV2.tsx.
  | "intake_v2_view"
  | "intake_v2_continue"
  | "intake_v2_back"
  | "intake_v2_error"
  | "intake_v2_va_switch_shown"
  | "intake_v2_va_switch_accepted"
  | "intake_v2_va_switch_declined"
  | "intake_v2_upload_started"
  | "intake_v2_upload_completed"
  | "intake_v2_upload_failed"
  | "intake_v2_upload_removed"
  | "intake_v2_submit_success"
  | "intake_v2_submit_failure";

export interface JourneyEvent {
  k: EventKind;
  /** Short payload (path, slug, label, etc). Truncated to 200 chars. */
  v: string;
  /** Optional secondary payload (href, etc). Truncated to 200 chars. */
  h?: string;
  /** ms since session start. */
  t: number;
}

interface JourneyState {
  visitor_id: string;
  session_id: string;
  visitor_kind: "new" | "returning";
  session_started_ms: number;
  landing_path: string;
  referrer: string;
  utm: Record<string, string>;
  language: string;
  events: JourneyEvent[];
  /** count of events already sent to the server; slice(sent_up_to) = unsent */
  sent_up_to: number;
  last_flush_ms: number;
}

const STORAGE_KEY = "cl_journey_v2";
const VISITOR_COOKIE = "cl_visitor";
const MAX_EVENTS = 100;
const MAX_STR = 200;

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

/** Central consent check. Extend when the cookie banner ships. */
export function journeyEnabled(): boolean {
  if (!isBrowser()) return false;
  try {
    // Explicit "essential-only" opts a visitor out of journey capture.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (w.__cl_consent === "essential-only") return false;
    const ls = window.localStorage?.getItem("cl_consent");
    if (ls === "denied" || ls === "essential-only") return false;

    // Internal / admin traffic is never in the visitor journey. This
    // covers /internal/* routes (dashboard, oxbow-verify, etc). Also
    // honor an opt-out flag (cl_internal_traffic=1) so QA/test browsers
    // can be permanently excluded from lead scoring, behavioral intent,
    // duplicate alerts, queues, digests, and production analytics.
    try {
      if (typeof window.location?.pathname === "string" && window.location.pathname.startsWith("/internal")) {
        return false;
      }
      if (window.localStorage?.getItem("cl_internal_traffic") === "1") return false;
    } catch {
      // ignore
    }

    // Skip analytics capture for blocked regions — the visitor is gated
    // by GeoGate on the client and by geoBlock on edge functions, so
    // recording their events would only produce dead rows and 403s.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (
        tz.startsWith("Asia/Shanghai") ||
        tz.startsWith("Asia/Urumqi") ||
        tz.startsWith("Asia/Chongqing") ||
        tz.startsWith("Asia/Harbin") ||
        tz.startsWith("Asia/Hong_Kong") ||
        tz.startsWith("Asia/Macau")
      ) {
        return false;
      }
    } catch {
      // ignore
    }
  } catch {
    // ignore
  }
  return true;
}

function truncate(s: string | null | undefined, n = MAX_STR): string {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) : s;
}

function uuid(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c: any = (globalThis as any).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (!isBrowser()) return;
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
}

function parseUtm(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!search) return out;
  try {
    const p = new URLSearchParams(search);
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"]) {
      const v = p.get(k);
      if (v) out[k] = truncate(v);
    }
  } catch {
    // ignore
  }
  return out;
}

function readState(): JourneyState | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as JourneyState;
  } catch {
    return null;
  }
}

function writeState(state: JourneyState): void {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage full or blocked — silently drop
  }
}

function ensureState(): JourneyState | null {
  if (!isBrowser() || !journeyEnabled()) return null;
  const existing = readState();
  if (existing) {
    // Backfill fields introduced after this state was first written
    // (older sessionStorage snapshots may be missing session_id / visitor_id).
    let mutated = false;
    if (!existing.session_id) { existing.session_id = uuid(); mutated = true; }
    if (!existing.visitor_id) {
      const vid = readCookie(VISITOR_COOKIE) ?? uuid();
      writeCookie(VISITOR_COOKIE, vid, 365);
      existing.visitor_id = vid;
      mutated = true;
    }
    if (mutated) writeState(existing);
    return existing;
  }

  let visitorId = readCookie(VISITOR_COOKIE);
  const isReturning = !!visitorId;
  if (!visitorId) {
    visitorId = uuid();
    writeCookie(VISITOR_COOKIE, visitorId, 365);
  }

  const state: JourneyState = {
    visitor_id: visitorId,
    session_id: uuid(),
    visitor_kind: isReturning ? "returning" : "new",
    session_started_ms: Date.now(),
    landing_path: truncate(window.location.pathname + window.location.search),
    referrer: truncate(document.referrer),
    utm: parseUtm(window.location.search),
    language: truncate((navigator.language || "").slice(0, 20)),
    events: [],
    sent_up_to: 0,
    last_flush_ms: 0,
  };
  writeState(state);
  return state;
}

/** Record one meaningful event. No-op when consent is denied. */
export function record(kind: EventKind, value: string, href?: string): void {
  const s = ensureState();
  if (!s) return;

  const v = truncate(value);
  const h = href ? truncate(href) : undefined;

  // Dedupe: skip if the same kind+value was recorded within the last 750ms
  // (guards against React double-mount in dev and duplicate route effects).
  const last = s.events[s.events.length - 1];
  const now = Date.now();
  if (last && last.k === kind && last.v === v && (now - s.session_started_ms) - last.t < 750) {
    return;
  }

  s.events.push({ k: kind, v, h, t: now - s.session_started_ms });
  if (s.events.length > MAX_EVENTS) {
    const dropped = s.events.length - MAX_EVENTS;
    s.events.splice(0, dropped);
    s.sent_up_to = Math.max(0, s.sent_up_to - dropped);
  }
  writeState(s);
  scheduleFlush();
}

function parseDevice(ua: string): { device: string; browser: string } {
  const u = ua.toLowerCase();
  let device = "desktop";
  if (/ipad|tablet/.test(u)) device = "tablet";
  else if (/mobi|iphone|android/.test(u)) device = "mobile";

  let browser = "other";
  if (/edg\//.test(u)) browser = "Edge";
  else if (/chrome\//.test(u) && !/edg\//.test(u)) browser = "Chrome";
  else if (/firefox\//.test(u)) browser = "Firefox";
  else if (/safari\//.test(u) && !/chrome\//.test(u)) browser = "Safari";

  return { device, browser };
}

/**
 * Snapshot the journey for a form submission. Safe to call even when
 * consent is denied — returns `null` in that case so the caller can
 * omit the field cleanly.
 */
export interface JourneySnapshot {
  visitor_id: string;
  session_id: string;
  visitor_kind: "new" | "returning";
  session_duration_ms: number;
  landing_path: string;
  referrer: string;
  utm: Record<string, string>;
  language: string;
  device: string;
  browser: string;
  user_agent: string;
  tz: string;
  events: JourneyEvent[];
  counts: {
    page_views: number;
    project_views: number;
    service_views: number;
    cta_clicks: number;
  };
  captured_at: string;
}

function safeTz(): string {
  try {
    return truncate(Intl.DateTimeFormat().resolvedOptions().timeZone || "", 60);
  } catch {
    return "";
  }
}

export function snapshotJourney(): JourneySnapshot | null {
  if (!isBrowser() || !journeyEnabled()) return null;
  const s = ensureState();
  if (!s) return null;
  const ua = navigator.userAgent || "";
  const { device, browser } = parseDevice(ua);

  const counts = {
    page_views: 0,
    project_views: 0,
    service_views: 0,
    cta_clicks: 0,
  };
  for (const e of s.events) {
    if (e.k === "page_view") counts.page_views++;
    else if (e.k === "project_view") counts.project_views++;
    else if (e.k === "service_view") counts.service_views++;
    else if (e.k === "cta_click") counts.cta_clicks++;
  }

  return {
    visitor_id: s.visitor_id,
    session_id: s.session_id,
    visitor_kind: s.visitor_kind,
    session_duration_ms: Date.now() - s.session_started_ms,
    landing_path: s.landing_path,
    referrer: s.referrer,
    utm: s.utm,
    language: s.language,
    device,
    browser,
    user_agent: truncate(ua, 300),
    tz: safeTz(),
    events: s.events.slice(),
    counts,
    captured_at: new Date().toISOString(),
  };
}


// -------------------- Server flushing --------------------

/** Publishable Supabase URL — same as the browser client uses. */
function ingestUrl(): string | null {
  const base = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL;
  return base ? `${base}/functions/v1/ingest-events` : null;
}
function ingestKey(): string | null {
  return (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_PUBLISHABLE_KEY ?? null;
}

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushInFlight = false;
const FLUSH_DEBOUNCE_MS = 4000;
const FLUSH_MIN_GAP_MS = 2000;

async function doFlush(useBeacon = false): Promise<void> {
  if (!isBrowser() || !journeyEnabled()) return;
  const s = readState();
  if (!s) return;
  const unsent = s.events.slice(s.sent_up_to);
  if (unsent.length === 0) return;
  const url = ingestUrl();
  const key = ingestKey();
  if (!url || !key) return;

  const now = Date.now();
  const meta = {
    landing_path: s.landing_path,
    referrer: s.referrer,
    utm: s.utm,
    language: s.language,
    device: parseDevice(navigator.userAgent || "").device,
    browser: parseDevice(navigator.userAgent || "").browser,
    tz: safeTz(),
    session_duration_ms: now - s.session_started_ms,
    consent_state: journeyEnabled() ? "granted" : "denied",
  };
  const payload = {
    visitor_id: s.visitor_id,
    session_id: s.session_id,
    meta,
    events: unsent.map((e) => ({
      k: e.k,
      v: e.v,
      h: e.h,
      route: e.h,
      age_ms: Math.max(0, now - (s.session_started_ms + e.t)),
    })),
  };

  const body = JSON.stringify(payload);
  try {
    if (useBeacon && "sendBeacon" in navigator) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url + `?apikey=${encodeURIComponent(key)}`, blob);
    } else {
      flushInFlight = true;
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "apikey": key,
          "Authorization": `Bearer ${key}`,
        },
        body,
        keepalive: true,
      });
    }
    const updated = readState();
    if (updated) {
      updated.sent_up_to = updated.events.length;
      updated.last_flush_ms = now;
      writeState(updated);
    }
  } catch {
    // swallow — we'll retry on next flush
  } finally {
    flushInFlight = false;
  }
}

/** Schedule a batched flush of unsent events. */
export function scheduleFlush(): void {
  if (!isBrowser() || !journeyEnabled()) return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (!flushInFlight) void doFlush(false);
  }, FLUSH_DEBOUNCE_MS);
}

/** Force an immediate flush (uses sendBeacon on unload). */
export function flushNow(useBeacon = false): void {
  const s = readState();
  if (!s) return;
  if (!useBeacon && Date.now() - s.last_flush_ms < FLUSH_MIN_GAP_MS) return;
  void doFlush(useBeacon);
}

if (isBrowser()) {
  // Flush on tab hide / unload to preserve the final events.
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushNow(true);
  });
  window.addEventListener("pagehide", () => flushNow(true));
}
